import io from 'socket.io-client';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import wsLogger from 'shared/lib/logWsEvent';

const initialState = {
  id: null,
  socket: null,
};

const WsContext = React.createContext(initialState);

const WsContextProvider = ({ config, apiClient, authStateSelector, children }) => {
  const logWsEvent = wsLogger(config);
  const wsServer = config.developmentMode ? `ws://${config.app.host}:${config.app.port}0` : '';
  const socket = useRef();
  const dispatch = useDispatch();
  const [socketId, setSocketId] = useState(null);
  const { loggedIn, token, user } = useSelector(authStateSelector);

  const userId = user && user.id ? user.id : null;

  useEffect(() => {
    if (loggedIn && userId && token) {
      socket.current = io(wsServer || '', { path: '/_ws', query: { token }, secure: true });
      socket.current.on('connect', () => {
        logWsEvent('connect', socket.current);
        setSocketId(socket.current.id);
        // connect event includes also re-connection, no need to handle it separatelly
        apiClient.setSocketId(socket.current.id);
      });

      socket.current.on('update', (event) => {
        const skip = event.skipFor === userId;
        logWsEvent('update', event, skip);
        if (event.type && !skip) {
          // convert ws event to action with suffix @UPDATE
          dispatch({ type: `${event.type}@UPDATE`, payload: event.payload });
        }
      });

      socket.current.callApi = (method, endpoint, payload, onSuccess, onError) => {
        /*
          TODO: mozna ukladat do mapy
          socket.current.responseId = {
            '/fooo/bar': 'random responseId'
          }
          a pred volanim likvidovat pouze "duplicity"
          pak by melo byt mozne pouzivat a vice mistech najednou
        */
        if (socket.current.responseId) {
          // console.log('remove', socket.current.responseId);
          socket.current.removeAllListeners(`call_api_success_${socket.current.responseId}`);
          socket.current.removeAllListeners(`call_api_error_${socket.current.responseId}`);
          socket.current.removeAllListeners(`call_api_end_${socket.current.responseId}`);
        }
        socket.current.emit('call_api', {
          method,
          endpoint,
          payload
        }, (responseId) => {
          socket.current.responseId = responseId;
          // console.log('set', socket.current.responseId);
          socket.current.on(`call_api_success_${responseId}`, data => onSuccess(data));
          socket.current.on(`call_api_error_${responseId}`, err => onError(err));
          socket.current.on(`call_api_end_${responseId}`, () => {
            // console.log('end', socket.current.responseId);
            socket.current.removeAllListeners(`call_api_success_${responseId}`);
            socket.current.removeAllListeners(`call_api_error_${responseId}`);
            socket.current.removeAllListeners(`call_api_end_${responseId}`);
          });
          // console.log(socket.current._callbacks);
        });

        return () => {
          if (socket.current.responseId) {
            // console.log('abort', socket.current.responseId);
            socket.current.removeAllListeners(`call_api_success_${socket.current.responseId}`);
            socket.current.removeAllListeners(`call_api_error_${socket.current.responseId}`);
            socket.current.removeAllListeners(`call_api_end_${socket.current.responseId}`);
          }
        };
      };
    } else if (socket.current) {
      try {
        socket.current.close();
      } catch {}
      socket.current = null;
      setSocketId(null);
    }
  }, [loggedIn, token, userId, dispatch]);

  return (
    <WsContext.Provider value={{ socketId, socket: socket.current }}>
      {children}
    </WsContext.Provider>
  );
};

// eslint-disable-next-line
export const useWsContext = () => {
  const context = React.useContext(WsContext);
  if (context === undefined) {
    throw new Error('WsContext must be within WsContextProvider');
  }

  return context;
};

export default WsContextProvider;
